import { useEffect, useState } from "react";
import { Youtube, RefreshCw, Save, CheckCircle2, Shapes } from "lucide-react";
import { toast } from "sonner";
import { getYoutubeConfig, saveYoutubeConfig, syncYoutube, reclassifyYoutube } from "@/lib/api";

const inputCls =
  "w-full bg-[#0D0D10] border border-white/15 text-white text-sm px-3 py-2.5 focus:border-[#FFD400] focus:outline-none transition-colors placeholder:text-slate-600";

export default function AdminYoutube({ onSynced }) {
  const [channelUrl, setChannelUrl] = useState("");
  const [aiArticles, setAiArticles] = useState(true);
  const [autoPublish, setAutoPublish] = useState(true);
  const [cfg, setCfg] = useState({ configured: false, channel_id: "", channel_title: "" });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [filing, setFiling] = useState(false);

  useEffect(() => {
    getYoutubeConfig()
      .then((c) => {
        setCfg(c);
        setChannelUrl(c.channel_url || "");
        setAiArticles(c.ai_articles);
        setAutoPublish(c.auto_publish);
      })
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await saveYoutubeConfig(channelUrl, aiArticles, autoPublish);
      setCfg({ configured: true, channel_id: res.channel_id, channel_title: res.channel_title });
      toast.success(`Channel linked${res.channel_title ? `: ${res.channel_title}` : ""}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to link channel");
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await syncYoutube();
      toast.success(`Imported ${res.imported} videos as articles (${res.skipped} already there)`);
      onSynced?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const refile = async () => {
    setFiling(true);
    try {
      const res = await reclassifyYoutube();
      toast.success(`Re-filed ${res.reclassified} of ${res.total} videos into news desks`);
      onSynced?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Re-filing failed");
    } finally {
      setFiling(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="admin-youtube">
      <div className="border border-white/10 p-6">
        <h3 className="font-editorial font-bold text-white text-lg flex items-center gap-2">
          <Youtube size={18} className="text-[#FF375F]" /> YouTube Video Import
        </h3>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          Pulls your latest videos from the channel feed — thumbnail, title and an article written from the video — and
          files them under the <span className="text-[#FF375F] font-semibold">वीडियो</span> category. No API key needed.
        </p>
        {cfg.configured && (
          <p className="mt-4 flex items-center gap-2 text-sm text-[#30D158]" data-testid="youtube-configured-badge">
            <CheckCircle2 size={14} /> Linked{cfg.channel_title ? ` — ${cfg.channel_title}` : ""}
            {cfg.channel_id ? ` (${cfg.channel_id})` : ""}
          </p>
        )}
        <form onSubmit={save} className="mt-5 space-y-4">
          <label className="block">
            <span className="font-mono-meta text-[10px] text-slate-400 uppercase tracking-widest block mb-1.5">
              Channel URL or @handle
            </span>
            <input
              data-testid="youtube-channel-input"
              className={inputCls}
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              placeholder="https://www.youtube.com/@khabriknews"
            />
          </label>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                data-testid="youtube-ai-checkbox"
                checked={aiArticles}
                onChange={(e) => setAiArticles(e.target.checked)}
                className="accent-[#FFD400] w-4 h-4"
              />
              Write a full news article from each video with AI
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                data-testid="youtube-autopublish-checkbox"
                checked={autoPublish}
                onChange={(e) => setAutoPublish(e.target.checked)}
                className="accent-[#FFD400] w-4 h-4"
              />
              Publish imported videos immediately
            </label>
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              data-testid="youtube-save-button"
              className="flex items-center gap-2 border border-white/15 text-slate-300 px-5 py-2.5 text-sm font-semibold hover:text-[#FFD400] hover:border-[#FFD400] transition-colors disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Linking..." : "Save Channel"}
            </button>
            <button
              type="button"
              onClick={sync}
              disabled={syncing || !cfg.configured}
              data-testid="youtube-sync-button"
              className="flex items-center gap-2 bg-[#FFD400] text-[#0D0D10] px-5 py-2.5 text-sm font-bold hover:bg-[#E6BE00] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Importing..." : "Import Videos"}
            </button>
          </div>
          <button
            type="button"
            onClick={refile}
            disabled={filing || !cfg.configured}
            data-testid="youtube-reclassify-button"
            className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-[#FFD400] transition-colors disabled:opacity-50"
          >
            <Shapes size={14} className={filing ? "animate-pulse" : ""} />
            {filing ? "Re-filing..." : "Re-file existing videos into news desks"}
          </button>
        </form>
      </div>
      <div className="border border-white/10 p-6">
        <h3 className="font-editorial font-bold text-white text-lg">How it works</h3>
        <ol className="mt-4 space-y-3">
          {[
            "Paste your channel URL (e.g. https://www.youtube.com/@khabriknews) and click Save Channel.",
            "Click Import Videos — the latest 15 uploads are fetched with their thumbnails.",
            "AI writes the report from the title and description and files each video into the right desk (भारत, विश्व, खेल, व्यापार, मनोरंजन, टेक).",
            "The video plays inside the article page, appears in the homepage Shorts reel, and stays browsable under वीडियो.",
            "New uploads are imported automatically every 3 hours — already-imported videos are never duplicated.",
          ].map((s, i) => (
            <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
              <span className="font-display text-[#FFD400] flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
              {s}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
